import classnames from 'classnames';
import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { withIncrementers } from 'components/shared/Percent/WithIncrementers';
import { useTenorUnits } from 'hooks/use-tenor-units';
import type { IntlKey } from 'locales';
import { FC, KeyboardEvent, PropsWithChildren, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { getTenorFieldUnitLabel } from 'utils/tenor/units';

export interface TenorFieldValue {
  unit: string;
  value?: number;
}

export interface TenorFieldData {
  currentValue: TenorFieldValue;
  isCustomized: boolean;
}

export interface TenorFieldUnitOption {
  label: string;
  shortcut?: string;
  value: string;
}

export interface TenorFieldProps {
  placeholder: string;
  availableUnits: TenorFieldUnitOption[];
  className?: string;
  e2e?: string;
  id?: string;
  isCustomized?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  value: TenorFieldValue;
  tabIndex?: number;
  prependLabelKey?: IntlKey;
  hasErrors?: boolean;
  onChange?(value: TenorFieldValue): void;
  onUncustomize?(): void;
  hasIncrementers?: boolean;
  incrementersLayout?: 'end' | 'around';
  onKeyDown?: (e: KeyboardEvent) => void;
  isFocused?: boolean;
  isSelectAll?: boolean;
  lostInputTenorFieldValue?: TenorFieldValue;
}

export const PrependLabel: FC<
  PropsWithChildren<{
    labelKey: IntlKey;
  }>
> = ({ labelKey }) => {
  const { formatMessage } = useIntl();
  return <span className="input-group-text">{formatMessage({ id: labelKey })}</span>;
};

export const TenorField: FC<TenorFieldProps> = ({
  placeholder,
  availableUnits,
  className,
  id,
  isCustomized = false,
  isDisabled = false,
  isReadOnly = false,
  prependLabelKey,
  onChange,
  onUncustomize,
  size = 'md',
  value,
  hasErrors,
  tabIndex,
  hasIncrementers = false,
  incrementersLayout,
  onKeyDown,
  isFocused,
  lostInputTenorFieldValue,
  isSelectAll,
}) => {
  const defaultTenorValue: TenorFieldValue = {
    value: undefined,
    unit: availableUnits.length > 0 ? availableUnits[0].value : 'Month',
  };
  const tenorValue: TenorFieldValue = value || defaultTenorValue;

  const { onInputKeyPress, unit, setUnit } = useTenorUnits({ initialUnit: tenorValue.unit, availableUnits });

  const intl = useIntl();

  const onValueChange = useCallback(
    (newValue?: number) => {
      if (onChange && (tenorValue.value !== newValue || tenorValue.unit !== unit || lostInputTenorFieldValue)) {
        onChange({
          value: newValue,
          unit: unit,
        });
      }
    },
    [lostInputTenorFieldValue, onChange, tenorValue.unit, tenorValue.value, unit],
  );

  useEffect(() => {
    setUnit(tenorValue.unit);
  }, [tenorValue, setUnit]);

  const onUncustomizeChange = useCallback(() => {
    if (onUncustomize) {
      onUncustomize();
    }
  }, [onUncustomize]);

  const AmountWithIncrementers = hasIncrementers
    ? withIncrementers(AmountWithUnit, { layout: incrementersLayout })
    : AmountWithUnit;

  return (
    <div className={classnames('tenor-field-component', `input-group input-group-${size}`, 'px-0')} id={id}>
      {isCustomized ? (
        <>
          {prependLabelKey && <PrependLabel labelKey={prependLabelKey} />}
          <input
            type="text"
            className="form-control text-uppercase"
            value={intl.formatMessage({ id: 'form.Tenor.custom' })}
            data-e2e={`${id}-is-custom`}
            readOnly
          />
          <button
            className="btn btn-discreet-secondary"
            type="button"
            role="tooltip"
            data-e2e={`${id}-decustomize`}
            onClick={onUncustomizeChange}
            disabled={isDisabled || isReadOnly}
          >
            <i className="icon icon-sm">cancel</i>
          </button>
        </>
      ) : (
        <AmountWithIncrementers
          accelerators={[]}
          className={classnames('form-control', `form-control-${size}`, className)}
          e2e={id !== undefined ? `${id}` : ''}
          incrementStep={1}
          precision={0}
          decimalPadding={false}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onBlur={onValueChange}
          onKeyPress={onInputKeyPress}
          value={lostInputTenorFieldValue?.value ?? tenorValue.value}
          tabIndex={tabIndex}
          placeholder={placeholder}
          hasError={hasErrors}
          unit={getTenorFieldUnitLabel(availableUnits, unit)}
          unitE2e={`${id}-unit`}
          isFocused={isFocused}
          isSelectAll={isSelectAll}
          onKeyDown={onKeyDown}
        >
          {prependLabelKey && <PrependLabel labelKey={prependLabelKey} />}
        </AmountWithIncrementers>
      )}
    </div>
  );
};
